/**
 * @file Compact Ordering card rendering for Phase 3 board foundation.
 */

import {
    convertIdsToNamesForEditing,
    escapeHTML,
} from "../../shared/utils/contentUtils.js";
import { getUserInitials } from "../../shared/utils/userDisplay.js";

function formatNeededByDate(dateValue) {
    if (!dateValue) {return "";}
    const canonicalDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
    return canonicalDate ?
        `${canonicalDate[3]}/${canonicalDate[2]}/${canonicalDate[1]}` :
        dateValue;
}

function formatCompactTimestamp(value) {
    const seconds = value?.seconds ?? value?._seconds;
    const date = value?.toDate?.() ||
        (value instanceof Date ? value : null) ||
        (typeof seconds === "number" ? new Date(seconds * 1000) : null);
    if (!date || Number.isNaN(date.getTime())) {return "";}
    return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function renderCardMetadata(item) {
    const neededBy = formatNeededByDate(item?.due_date);
    const requestedOn = formatCompactTimestamp(item?.createdAt);
    const requester = item?.createdBy && requestedOn ?
        `Requested by ${getUserInitials(item.createdBy)} on ${requestedOn}` : "";
    if (!neededBy && !requester) {return "";}
    return `
        <p class="mt-1 text-xs text-gray-500" data-ordering-card-metadata>
            ${neededBy ? `<span data-ordering-needed-by>${escapeHTML(neededBy)}</span>` : ""}
            ${requester ? `<span class="${neededBy ? "ml-2" : ""}" data-ordering-requester>${escapeHTML(requester)}</span>` : ""}
        </p>
    `;
}

/**
 * Renders a compact, read-only-until-Phase-4 Ordering card.
 * @param {object} item Ordering item.
 * @returns {string} Card markup.
 */
export function renderOrderingCard(item) {
    const isPendingCreate = item?.__orderingPending === "create";
    const details = String(item?.details || "").trim();
    const latestNote = String(item?.latestNote?.text || "").trim();
    const latestNoteText = convertIdsToNamesForEditing(latestNote);
    if (isPendingCreate) {
        return `
            <article class="ordering-card rounded-md border border-slate-100 bg-gray-50 p-3" data-ordering-item-id="${escapeHTML(item.id)}" data-ordering-pending-create="true">
                <h4 class="break-words text-sm font-semibold leading-5 text-gray-900">${escapeHTML(item.name || "Untitled item")}</h4>
                ${renderCardMetadata(item)}
                ${details ? `<p class="mt-1.5 overflow-hidden text-xs leading-5 text-gray-500 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">${escapeHTML(details)}</p>` : ""}
            </article>
        `;
    }
    const statusActions = renderStatusActions(item);
    return `
        <article class="ordering-card group cursor-pointer rounded-md border border-slate-100 bg-gray-50 p-3 transition-colors hover:bg-gray-100 focus-within:bg-gray-100" data-ordering-item-id="${escapeHTML(item.id)}">
            <button type="button" class="ordering-card-open block min-w-0 w-full rounded-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500/60" aria-label="Edit ${escapeHTML(item.name || "Ordering item")}">
                <h4 class="break-words text-sm font-semibold leading-5 text-gray-900">${escapeHTML(item.name || "Untitled item")}</h4>
                ${renderCardMetadata(item)}
                ${details ? `<p class="mt-1.5 overflow-hidden text-xs leading-5 text-gray-500 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">${escapeHTML(details)}</p>` : ""}
                ${latestNote ? `
                    <div class="mt-2 flex min-w-0 items-center border-t border-gray-200 pt-1.5 text-gray-500 dark:text-gray-400">
                        <span class="material-symbols-outlined mr-1.5 flex-shrink-0 text-[14px]" data-ordering-note-icon aria-hidden="true">chat_bubble</span>
                        <p class="min-w-0 overflow-hidden text-xs italic leading-5 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">${escapeHTML(latestNoteText)}</p>
                    </div>
                ` : ""}
            </button>
            ${statusActions ? `<div class="mt-0.5 flex cursor-default items-center justify-end" data-ordering-status-actions>${statusActions}</div>` : ""}
        </article>
    `;
}

const ACTIONS_BY_STATUS = Object.freeze({
    open: [
        {status: "ordered", icon: "shopping_cart", label: "Mark ordered"},
        {status: "cancelled", icon: "cancel", label: "Cancel item"},
    ],
    ordered: [
        {status: "open", icon: "shopping_cart", label: "Undo ordered", active: "ordered"},
        {status: "delivered", icon: "package_2", label: "Mark delivered"},
        {status: "cancelled", icon: "cancel", label: "Cancel item"},
    ],
    delivered: [
        {status: "ordered", icon: "package_2", label: "Undo delivered", active: "delivered"},
    ],
    cancelled: [
        {status: "open", icon: "cancel", label: "Reopen cancelled item", active: "cancelled"},
    ],
});

function activeStatusClasses(status) {
    if (status === "ordered") {
        return "text-green-700 hover:text-green-700 focus-visible:text-green-700";
    }
    if (status === "delivered") {
        return "text-green-700 hover:text-green-700 focus-visible:text-green-700";
    }
    if (status === "cancelled") {
        return "text-black hover:text-black focus-visible:text-black";
    }
    return "";
}

function targetStatusClasses(status) {
    if (status === "ordered") {return "hover:text-red-600 focus-visible:text-red-600";}
    if (status === "delivered") {return "hover:text-[var(--dfc-brand-primary)] focus-visible:text-[var(--dfc-brand-primary)]";}
    if (status === "cancelled") {return "hover:text-red-600 focus-visible:text-red-600";}
    return "hover:text-gray-900 focus-visible:text-gray-900";
}

function renderStatusActions(item) {
    return (ACTIONS_BY_STATUS[item.status] || []).map((action) => `
        <button type="button" class="ordering-status-action inline-flex h-5 w-6 items-center justify-center border-0 bg-transparent text-gray-400 transition-colors ${targetStatusClasses(action.status)} focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500/60 ${activeStatusClasses(action.active)}" data-target-status="${action.status}" aria-label="${action.label}" title="${action.label}">
            <span class="material-symbols-outlined text-[17px] leading-none" aria-hidden="true">${action.icon}</span>
        </button>
    `).join("");
}
